'use client';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { SearchForm } from '@/components/blog/Search/SearchForm';

import LazyMotionWrapper from '@/components/shared/LazyMotionWrapper';

// import { Select, Option } from '@material-tailwind/react';

import {
  // Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { CaretDown, Check } from '@phosphor-icons/react';
import { useAtom, useSetAtom } from 'jotai';
import {
  currentPaginationPageAtom,
  initializeFiltersFromUrlAtom,
  initializePaginationPageFromUrlAtom,
  initializeSearchQueryFromUrlAtom,
  isBlogSearchReadyAtom,
  searchFiltersAtom,
  searchQueryAtom,
} from '@/store/blogStore';

export default function BlogSearch({
  query,
  page,
  categories,
  tags,
  initialSearchFilters,
}: {
  query?: string;
  page?: number;
  categories: {
    name: string;
    slug: string;
  }[];
  tags: {
    name: string;
    slug: string;
  }[];
  initialSearchFilters?: {
    category: string | null;
    tag: string | null;
  };
}) {
  // initialize
  const setInitializeSearchQueryFromUrl = useSetAtom(
    initializeSearchQueryFromUrlAtom
  );
  // initializePaginationPageFromUrlAtom
  const setInitializePaginationPageFromUrl = useSetAtom(
    initializePaginationPageFromUrlAtom
  );
  // initializeFiltersFromUrlAtom
  const setInitializeFiltersFromUrl = useSetAtom(initializeFiltersFromUrlAtom);
  const setSearchQuery = useSetAtom(searchQueryAtom); // Use the atom
  const [currentPage, setCurrentPage] = useAtom(currentPaginationPageAtom);
  const [searchFilters, setSearchFilters] = useAtom(searchFiltersAtom);

  const initQueryCount = useRef(0);
  const setBlogSearchIsReady = useSetAtom(isBlogSearchReadyAtom);

  React.useEffect(() => {
    setInitializeSearchQueryFromUrl();

    initQueryCount.current += 1;
  }, [query]);

  React.useEffect(() => {
    setInitializePaginationPageFromUrl();

    initQueryCount.current += 1;
  }, [page, setCurrentPage]);

  useEffect(() => {
    setInitializeFiltersFromUrl();
    initQueryCount.current += 1;
  }, [initialSearchFilters, setSearchFilters]);

  useEffect(() => {
    if (initQueryCount.current === 3) {
      setBlogSearchIsReady(true);
      initQueryCount.current = 0;
    }
  }, [initQueryCount]);

  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [categorySearch, setCategorySearch] = useState('');
  const [filteredCategories, setFilteredCategories] = useState(categories);

  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState(tags);

  useEffect(() => {
    setFilteredCategories(
      categories.filter((category) =>
        category.name.toLowerCase().includes(categorySearch.toLowerCase())
      )
    );
  }, [categorySearch, categories]);

  useEffect(() => {
    setFilteredTags(
      tags.filter((tag) =>
        tag.name.toLowerCase().includes(tagSearch.toLowerCase())
      )
    );
  }, [tagSearch, tags]);

  const handleFocusChange = (isFocused: boolean) => {
    if (isFocused) {
      panelRef.current?.classList.remove('hidden');
    } else {
      panelRef.current?.classList.add('hidden');
    }
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    //  @ts-ignore
    setSearchQuery(e.target.value);
  };

  const debounce = (func: (...args: any[]) => void, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const debouncedHandleInput = debounce(handleInput, 250);

  const handleTagChange = (tag: string) => {
    setCurrentPage(1);
    setSearchFilters({ ...searchFilters, tag });
  };
  const handleCategoryChange = (category: string) => {
    setCurrentPage(1);
    setSearchFilters({ ...searchFilters, category });
  };

  // computed selected category name , cached
  const selectedCategoryName = useMemo(() => {
    return categories.find((cat) => cat.slug === searchFilters?.category)?.name;
  }, [searchFilters?.category, categories]);

  const selectedTagName = useMemo(() => {
    return tags.find((tag) => tag.slug === searchFilters?.tag)?.name;
  }, [searchFilters?.tag, tags]);

  return (
    <div className='container-padding container fixed left-0 right-0 top-0 !z-30 flex flex-col justify-between bg-white px-6 pb-2 pt-24 sm:px-10 lg:max-w-[1200px]'>
      <SearchForm
        className='peer'
        formRef={formRef}
        inputRef={inputRef}
        onFocus={() => handleFocusChange(true)}
        onBlur={() => handleFocusChange(false)}
        onInput={debouncedHandleInput}
        initialValue={query || ''}
      />
      <LazyMotionWrapper>
        <div className='relative z-20 w-full transition-all duration-200 md:w-1/2'>
          <div className={'z-200 mt-2 flex gap-4 sm:mt-5'}>
            <Listbox
              value={searchFilters?.category ?? undefined}
              onChange={handleCategoryChange}
            >
              <div className='relative mt-2 w-full'>
                <ListboxButton
                  className={`grid w-full cursor-pointer grid-cols-1 rounded-[10px] border border-[#EFE9DB] bg-[#F9F6F0] py-1.5 pl-3 pr-2 text-left text-gray-900 ${searchFilters?.category ? 'border border-[#FFCD00] bg-gradient-to-r from-[#FFCF41] to-[#FFE283]' : ''}`}
                >
                  <span className='col-start-1 row-start-1 flex items-center gap-3 pr-6'>
                    <span className='block truncate text-base text-charcoal sm:text-xl'>
                      {selectedCategoryName || 'All categories'}
                    </span>
                  </span>
                  <CaretDown
                    aria-hidden='true'
                    className='col-start-1 row-start-1 size-5 self-center justify-self-end text-charcoal sm:size-5'
                  />
                </ListboxButton>

                <ListboxOptions
                  transition
                  className='absolute z-10 mt-1 max-h-[20rem] w-full overflow-auto rounded-[10px] bg-white pb-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:max-h-[30rem] sm:text-sm'
                >
                  <div className='sticky top-0 z-20 bg-white px-3 py-2'>
                    <input
                      placeholder='Search categories'
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      className='w-full rounded-md border border-[#EFE9DB] bg-[#F9F6F0] px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-[#FFCD00] focus:outline-none'
                    />
                  </div>

                  <ListboxOption
                    value={null} // Clear selection
                    className='group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-[#F9F6F0] data-[focus]:outline-none'
                  >
                    <div className='flex items-center'>
                      <span className='ml-3 block truncate text-sm font-normal sm:text-base'>
                        All categories
                      </span>
                    </div>
                  </ListboxOption>

                  {filteredCategories.map((category) => (
                    <ListboxOption
                      key={category.slug}
                      value={category.slug}
                      className='group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-[#F9F6F0] data-[focus]:outline-none'
                    >
                      <div className='flex items-center'>
                        <span className='ml-3 block truncate text-sm font-normal group-data-[selected]:font-semibold sm:text-base'>
                          {category.name}
                        </span>
                      </div>

                      <span className='absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white'>
                        <Check aria-hidden='true' className='size-5' />
                      </span>
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
            <Listbox value={searchFilters?.tag ?? undefined} onChange={handleTagChange}>
              <div className='relative mt-2 w-full'>
                <ListboxButton
                  className={`grid w-full cursor-pointer grid-cols-1 rounded-[10px] border border-[#EFE9DB] bg-[#F9F6F0] py-1.5 pl-3 pr-2 text-left text-gray-900 ${searchFilters?.tag ? 'border border-[#FFCD00] bg-gradient-to-r from-[#FFCF41] to-[#FFE283]' : ''}`}
                >
                  <span className='col-start-1 row-start-1 flex items-center gap-3 pr-6'>
                    <span className='block truncate text-base text-charcoal sm:text-xl'>
                      {selectedTagName || 'All tags'}
                    </span>
                  </span>
                  <CaretDown
                    aria-hidden='true'
                    className='col-start-1 row-start-1 size-5 self-center justify-self-end text-charcoal sm:size-5'
                  />
                </ListboxButton>

                <ListboxOptions
                  transition
                  className='absolute z-10 mt-1 max-h-[20rem] w-full overflow-auto rounded-[10px] bg-white pb-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:max-h-[30rem] sm:text-sm'
                >
                  <div className='sticky top-0 z-20 bg-white px-3 py-2'>
                    <input
                      value={tagSearch}
                      placeholder='Search tags'
                      onChange={(e) => setTagSearch(e.target.value)}
                      className='w-full rounded-md border border-[#EFE9DB] bg-[#F9F6F0] px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-[#FFCD00] focus:outline-none'
                    />
                  </div>

                  <ListboxOption
                    value={null} // Clear selection
                    className='group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-[#F9F6F0] data-[focus]:outline-none'
                  >
                    <div className='flex items-center'>
                      <span className='ml-3 block truncate text-sm font-normal sm:text-base'>
                        All tags
                      </span>
                    </div>
                  </ListboxOption>

                  {filteredTags.map((tag) => (
                    <ListboxOption
                      key={tag.slug}
                      value={tag.slug}
                      className='group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-[#F9F6F0] data-[focus]:outline-none'
                    >
                      <div className='flex items-center'>
                        <span className='ml-3 block truncate text-sm font-normal group-data-[selected]:font-semibold sm:text-base'>
                          {tag.name}
                        </span>
                      </div>

                      <span className='absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white'>
                        <Check aria-hidden='true' className='size-5' />
                      </span>
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>
        </div>
      </LazyMotionWrapper>
    </div>
  );
}
